import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { X, Eye } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

const copy = {
  en: {
    heading: 'Messages that feel as meaningful as the words inside them',
    subtext: 'Beautifully designed announcements that help your words land the way they deserve to.',
    preview: 'Preview Message',
    close: 'Close',
    templates: [
      {
        label: 'Reflection Page',
        to: 'To: My Best Friend',
        lines: ['There are moments in life', 'when someone quietly', 'changes everything.', '', 'You were that person for me.', '', 'And I never properly said', 'thank you.', '', 'So here it is.', 'Thank you.'],
      },
      {
        label: 'Quote Reveal',
        badge: 'Most Shareable',
        lines: ['Sometimes the people', 'who change our lives the most', '', 'never realize', 'how deeply they mattered.', '', 'You are one of those people.'],
        footer: 'Say It NOW\nBecause the living can hear us now.',
      },
      {
        label: 'A Sincere Apology',
        lines: ['I\'ve been thinking about something.', '', 'The way I handled things', 'wasn\'t fair to you.', '', 'You deserved better from me.', '', 'I\'m truly sorry.'],
        signature: '— With sincerity',
      },
    ],
  },
  es: {
    heading: 'Mensajes que se sienten tan significativos como las palabras que contienen',
    subtext: 'Anuncios bellamente diseñados que ayudan a que tus palabras lleguen como merecen.',
    preview: 'Vista Previa',
    close: 'Cerrar',
    templates: [
      { label: 'Página de Reflexión', to: 'Para: Mi Mejor Amigo/a', lines: ['Hay momentos en la vida', 'cuando alguien cambia', 'todo silenciosamente.', '', 'Tú fuiste esa persona para mí.', '', 'Y nunca te di las gracias', 'como debía.', '', 'Así que aquí está.', 'Gracias.'] },
      { label: 'Revelación de Cita', badge: 'Más Compartido', lines: ['A veces las personas', 'que más cambian nuestras vidas', '', 'nunca se dan cuenta', 'de cuánto importaron.', '', 'Tú eres una de esas personas.'], footer: 'Say It NOW\nPorque los vivos pueden escucharnos ahora.' },
      { label: 'Una Disculpa Sincera', lines: ['He estado pensando en algo.', '', 'La forma en que manejé las cosas', 'no fue justa contigo.', '', 'Merecías algo mejor de mi parte.', '', 'Lo siento de verdad.'], signature: '— Con sinceridad' },
    ],
  },
  pt: {
    heading: 'Mensagens que parecem tão significativas quanto as palavras dentro delas',
    subtext: 'Anúncios lindamente projetados que ajudam suas palavras a chegarem como merecem.',
    preview: 'Visualizar',
    close: 'Fechar',
    templates: [
      { label: 'Página de Reflexão', to: 'Para: Meu Melhor Amigo/a', lines: ['Há momentos na vida', 'quando alguém muda', 'tudo silenciosamente.', '', 'Você foi essa pessoa para mim.', '', 'E eu nunca agradeci', 'como devia.', '', 'Então aqui está.', 'Obrigado.'] },
      { label: 'Revelação de Citação', badge: 'Mais Compartilhável', lines: ['Às vezes as pessoas', 'que mais mudam nossas vidas', '', 'nunca percebem', 'o quanto importaram.', '', 'Você é uma dessas pessoas.'], footer: 'Say It NOW\nPorque os vivos podem nos ouvir agora.' },
      { label: 'Um Pedido de Desculpas Sincero', lines: ['Tenho pensado sobre algo.', '', 'A forma como lidei com as coisas', 'não foi justa com você.', '', 'Você merecia mais de mim.', '', 'Sinto muito de verdade.'], signature: '— Com sinceridade' },
    ],
  },
  fr: {
    heading: 'Des messages aussi significatifs que les mots qu\'ils contiennent',
    subtext: 'Des annonces magnifiquement conçues pour que vos mots touchent comme ils le méritent.',
    preview: 'Aperçu',
    close: 'Fermer',
    templates: [
      { label: 'Page de Réflexion', to: 'À : Mon Meilleur Ami(e)', lines: ['Il y a des moments dans la vie', 'où quelqu\'un change', 'tout en silence.', '', 'Tu étais cette personne pour moi.', '', 'Et je ne t\'ai jamais remercié', 'comme il se doit.', '', 'Alors voilà.', 'Merci.'] },
      { label: 'Révélation de Citation', badge: 'Le Plus Partagé', lines: ['Parfois les personnes', 'qui changent le plus nos vies', '', 'ne réalisent jamais', 'à quel point elles comptaient.', '', 'Tu es l\'une de ces personnes.'], footer: 'Say It NOW\nParce que les vivants peuvent nous entendre maintenant.' },
      { label: 'Excuses Sincères', lines: ['J\'ai réfléchi à quelque chose.', '', 'La façon dont j\'ai géré les choses', 'n\'était pas juste envers toi.', '', 'Tu méritais mieux de ma part.', '', 'Je suis vraiment désolé.'], signature: '— Avec sincérité' },
    ],
  },
  ar: { heading: 'رسائل تبدو بنفس معنى الكلمات التي بداخلها', subtext: 'إعلانات مصممة بعناية تساعد كلماتك على الوصول كما تستحق.', preview: 'معاينة', close: 'إغلاق', templates: [{ label: 'صفحة تأمل', to: 'إلى: صديقي المقرب', lines: ['هناك لحظات في الحياة', 'عندما يغير شخص ما', 'كل شيء بهدوء.', '', 'أنت كنت ذلك الشخص بالنسبة لي.', '', 'ولم أشكرك أبدًا', 'كما ينبغي.', '', 'ها هي.', 'شكرًا.'] }, { label: 'كشف الاقتباس', badge: 'الأكثر مشاركة', lines: ['أحيانًا الأشخاص', 'الذين يغيرون حياتنا أكثر', '', 'لا يدركون أبدًا', 'كم كانوا مهمين.', '', 'أنت أحد هؤلاء الأشخاص.'], footer: 'Say It NOW\nلأن الأحياء يمكنهم سماعنا الآن.' }, { label: 'اعتذار صادق', lines: ['كنت أفكر في شيء.', '', 'الطريقة التي تعاملت بها مع الأمور', 'لم تكن عادلة معك.', '', 'كنت تستحق أفضل مني.', '', 'أنا آسف حقًا.'], signature: '— بصدق' }] },
  ja: { heading: '中の言葉と同じくらい意味のあるメッセージ', subtext: 'あなたの言葉がふさわしい形で届くよう、美しくデザインされたアナウンスメント。', preview: 'プレビュー', close: '閉じる', templates: [{ label: 'リフレクションページ', to: '宛先：親友', lines: ['人生には時々', '誰かが静かに', 'すべてを変える瞬間があります。', '', 'あなたは私にとってその人でした。', '', 'そして私はきちんと', 'お礼を言ったことがありませんでした。', '', 'だからここに。', 'ありがとう。'] }, { label: '引用リビール', badge: '最も共有される', lines: ['時々、私たちの人生を', '最も変えた人々は', '', '自分がどれほど', '大切だったか気づかない。', '', 'あなたはそんな人の一人です。'], footer: 'Say It NOW\n生きている人は今聞くことができるから。' }, { label: '心からの謝罪', lines: ['あることを考えていました。', '', '私の対応の仕方は', 'あなたに公平ではありませんでした。', '', 'あなたはもっと良い扱いを受けるべきでした。', '', '本当にごめんなさい。'], signature: '— 誠意を込めて' }] },
  ko: { heading: '안에 담긴 말만큼이나 의미 있는 메시지', subtext: '당신의 말이 마땅히 전해져야 할 방식으로 전달되도록 아름답게 디자인된 발표입니다.', preview: '미리보기', close: '닫기', templates: [{ label: '성찰 페이지', to: '받는 사람: 절친', lines: ['인생에는 때때로', '누군가가 조용히', '모든 것을 바꾸는 순간이 있습니다.', '', '당신이 저에게 그런 사람이었습니다.', '', '그리고 제대로', '감사를 전한 적이 없었습니다.', '', '여기 있습니다.', '감사합니다.'] }, { label: '인용 공개', badge: '가장 많이 공유됨', lines: ['때때로 우리의 삶을', '가장 많이 바꾼 사람들은', '', '자신이 얼마나', '깊이 중요했는지 깨닫지 못합니다.', '', '당신이 그런 사람 중 하나입니다.'], footer: 'Say It NOW\n살아있는 사람은 지금 들을 수 있으니까.' }, { label: '진심 어린 사과', lines: ['어떤 것에 대해 생각하고 있었습니다.', '', '제가 일을 처리한 방식은', '당신에게 공정하지 않았습니다.', '', '당신은 더 나은 대우를 받을 자격이 있었습니다.', '', '정말 죄송합니다.'], signature: '— 진심을 담아' }] },
  id: { heading: 'Pesan yang terasa sama bermakna seperti kata-kata di dalamnya', subtext: 'Pengumuman yang dirancang dengan indah untuk membantu kata-kata Anda sampai seperti yang seharusnya.', preview: 'Pratinjau', close: 'Tutup', templates: [{ label: 'Halaman Refleksi', to: 'Kepada: Sahabatku', lines: ['Ada momen dalam hidup', 'ketika seseorang diam-diam', 'mengubah segalanya.', '', 'Kamu adalah orang itu bagiku.', '', 'Dan aku tidak pernah', 'berterima kasih dengan semestinya.', '', 'Jadi ini dia.', 'Terima kasih.'] }, { label: 'Kutipan Terungkap', badge: 'Paling Dapat Dibagikan', lines: ['Terkadang orang-orang', 'yang paling mengubah hidup kita', '', 'tidak pernah menyadari', 'betapa berartinya mereka.', '', 'Kamu adalah salah satu dari mereka.'], footer: 'Say It NOW\nKarena yang hidup bisa mendengar kita sekarang.' }, { label: 'Permintaan Maaf Tulus', lines: ['Aku telah memikirkan sesuatu.', '', 'Cara aku menangani hal-hal', 'tidak adil bagimu.', '', 'Kamu pantas mendapat yang lebih baik dariku.', '', 'Aku benar-benar minta maaf.'], signature: '— Dengan tulus' }] },

  hi: {
    heading: 'ऐसे संदेश जो अंदर के शब्दों जितने ही सार्थक लगते हैं',
    subtext: 'खूबसूरती से डिज़ाइन की गई घोषणाएँ जो आपके शब्दों को उस तरह से पहुंचाने में मदद करती हैं जिसके वे हकदार हैं।',
    preview: 'संदेश का पूर्वावलोकन करें',
    close: 'बंद करें',
    templates: [
      {
        label: 'Reflection Page',
        to: 'मेरे सबसे अच्छे दोस्त को:',
        lines: ['There are moments in life', 'when someone quietly', 'changes everything.', '', 'You were that person for me.', '', 'And I never properly said', 'thank you.', '', 'So here it is.', 'Thank you.'],
      },
      {
        label: 'Quote Reveal',
        badge: 'सबसे अधिक साझा करने योग्य',
        lines: ['Sometimes the people', 'who change our lives the most', '', 'never realize', 'how deeply they mattered.', '', 'You are one of those people.'],
        footer: 'Say It NOW\nक्योंकि जीवित लोग हमें अब सुन सकते हैं।',
      },
      {
        label: 'एक सच्ची माफी',
        lines: ['I\'ve been thinking about something.', '', 'The way I handled things', 'wasn\'t fair to you.', '', 'You deserved better from me.', '', 'I\'m truly sorry.'],
        signature: '— ईमानदारी के साथ',
      },
    ],
  },
};

/* ── Ambient particles ── */

function AmbientParticles() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {/* Floating orbs */}
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full"
          style={{
            width: 80 + i * 40,
            height: 80 + i * 40,
            left: `${10 + i * 15}%`,
            top: `${20 + (i % 3) * 25}%`,
            background: `radial-gradient(circle, hsl(var(--primary) / ${0.06 + i * 0.01}), transparent 70%)`,
          }}
          animate={{
            y: [0, -20 - i * 5, 0],
            x: [0, 10 + i * 3, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 8 + i * 2,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 1.2,
          }}
        />
      ))}
      {/* Soft light wash */}
      <motion.div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 40%, hsl(var(--primary) / 0.04), transparent 60%)',
        }}
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      />
    </div>
  );
}

/* ── Card renderers ── */

function ReflectionCard({ tmpl, isModal = false }: { tmpl: any; isModal?: boolean }) {
  const textSize = isModal ? 'text-lg md:text-xl' : 'text-base';
  return (
    <div className="relative h-full overflow-hidden rounded-2xl"
      style={{
        background: 'linear-gradient(145deg, hsl(35 40% 95%), hsl(30 30% 90%), hsl(35 35% 88%))',
      }}
    >
      {/* Animated parchment texture */}
      <motion.div
        className="absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`,
          backgroundSize: '150px',
        }}
        animate={{ backgroundPosition: ['0px 0px', '5px 5px', '0px 0px'] }}
        transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
      />
      {/* Gold edge accent */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-400/30 to-transparent" />

      <div className={`relative ${isModal ? 'px-10 py-12 md:px-16 md:py-16' : 'px-6 pt-5 pb-7'}`}>
        <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-amber-700/70">{tmpl.label}</span>
        {'to' in tmpl && tmpl.to && (
          <p className="text-sm font-medium text-amber-800/60 mt-3 mb-5 italic">{tmpl.to}</p>
        )}
        <div className="space-y-0.5 mt-3">
          {tmpl.lines.map((line: string, li: number) => (
            <motion.p
              key={li}
              className={`font-serif ${textSize} text-amber-950/85 ${line === '' ? 'h-3' : ''} leading-relaxed italic`}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 + li * 0.07 }}
            >
              {line || '\u00A0'}
            </motion.p>
          ))}
        </div>
      </div>
    </div>
  );
}

function QuoteRevealCard({ tmpl, isModal = false }: { tmpl: any; isModal?: boolean }) {
  const textSize = isModal ? 'text-2xl md:text-3xl' : 'text-xl md:text-2xl';
  return (
    <div className="relative h-full overflow-hidden rounded-2xl"
      style={{
        background: 'linear-gradient(135deg, hsl(160 60% 20%), hsl(170 50% 28%), hsl(155 45% 22%))',
      }}
    >
      {/* Animated gradient glow */}
      <motion.div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 30%, hsl(160 80% 40% / 0.35), transparent 70%)',
        }}
        animate={{
          opacity: [0.3, 0.6, 0.3],
          scale: [1, 1.05, 1],
        }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      />
      {/* Noise texture */}
      <div className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`,
          backgroundSize: '120px',
        }}
      />

      <div className={`relative text-center ${isModal ? 'px-10 py-14 md:px-16 md:py-20' : 'px-6 pt-6 pb-8'}`}>
        <div className="flex items-center justify-center gap-2 mb-4">
          <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-emerald-300/70">{tmpl.label}</span>
          {'badge' in tmpl && tmpl.badge && (
            <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-400/20 text-emerald-300 px-2 py-0.5 rounded-full border border-emerald-400/20">
              {tmpl.badge}
            </span>
          )}
        </div>
        <div className="space-y-1 mt-5">
          {tmpl.lines.map((line: string, li: number) => (
            <motion.p
              key={li}
              className={`font-serif ${textSize} text-emerald-50/90 ${line === '' ? 'h-5' : ''} leading-[1.6]`}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 + li * 0.08 }}
            >
              {line || '\u00A0'}
            </motion.p>
          ))}
        </div>
        {'footer' in tmpl && tmpl.footer && (
          <div className="mt-8 pt-4 border-t border-emerald-400/15">
            {tmpl.footer.split('\n').map((l: string, fi: number) => (
              <p key={fi} className={`text-xs text-emerald-300/60 ${fi === 0 ? 'font-bold' : 'font-medium opacity-70'}`}>{l}</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function QuietApologyCard({ tmpl, isModal = false }: { tmpl: any; isModal?: boolean }) {
  const textSize = isModal ? 'text-lg md:text-xl' : 'text-base';
  return (
    <div className="relative h-full overflow-hidden rounded-2xl"
      style={{
        background: 'linear-gradient(160deg, hsl(220 40% 14%), hsl(225 35% 18%), hsl(230 30% 22%))',
      }}
    >
      {/* Animated light sweep */}
      <motion.div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(120deg, transparent 30%, hsl(220 30% 40% / 0.15) 50%, transparent 70%)',
        }}
        animate={{ x: ['-100%', '100%'] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', repeatDelay: 4 }}
      />
      {/* Subtle warm glow */}
      <div className="absolute inset-0 opacity-30"
        style={{
          background: 'radial-gradient(ellipse at 30% 80%, hsl(220 30% 35% / 0.4), transparent 60%)',
        }}
      />

      <div className={`relative ${isModal ? 'px-10 py-12 md:px-16 md:py-16' : 'px-6 pt-5 pb-7'}`}>
        <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400/70">{tmpl.label}</span>
        <div className="space-y-0.5 mt-4">
          {tmpl.lines.map((line: string, li: number) => (
            <motion.p
              key={li}
              className={`font-serif ${textSize} text-slate-200/80 ${line === '' ? 'h-3' : ''} leading-relaxed font-light`}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.25 + li * 0.07 }}
            >
              {line || '\u00A0'}
            </motion.p>
          ))}
        </div>
        {'signature' in tmpl && tmpl.signature && (
          <p className="mt-6 text-sm italic text-slate-400/50 font-light">{tmpl.signature}</p>
        )}
      </div>
    </div>
  );
}

const cardRenderers = [ReflectionCard, QuoteRevealCard, QuietApologyCard];

/* ── Modal ── */

function TemplatePreviewModal({ tmpl, index, onClose, closeLabel }: { tmpl: any; index: number; onClose: () => void; closeLabel: string }) {
  const Renderer = cardRenderers[index];
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="relative w-full max-w-lg"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute -top-3 -right-3 z-10 w-8 h-8 rounded-full bg-foreground/80 text-background flex items-center justify-center hover:bg-foreground transition-colors"
            aria-label={closeLabel}
          >
            <X className="w-4 h-4" />
          </button>
          <motion.div
            animate={{
              boxShadow: [
                '0 0 30px 0px hsl(var(--primary) / 0.15)',
                '0 0 50px 5px hsl(var(--primary) / 0.25)',
                '0 0 30px 0px hsl(var(--primary) / 0.15)',
              ],
            }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            className="rounded-2xl overflow-hidden"
          >
            <Renderer tmpl={tmpl} isModal />
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ── Main Section ── */

export function PremiumTemplateShowcase() {
  const { language } = useLanguage();
  const t = lc(copy, language);
  const [activeModal, setActiveModal] = useState<number | null>(null);
  const isMobile = useIsMobile();

  return (
    <>
      <section className="relative py-20 md:py-28 overflow-hidden">
        {/* Ambient background */}
        <AmbientParticles />

        <div className="container max-w-5xl mx-auto relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold billboard-flash !leading-tight max-w-3xl mx-auto">
              {t.heading}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              {t.subtext}
            </p>
          </motion.div>

          {/* Cards grid */}
          <div className={isMobile
            ? 'flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 scrollbar-hide'
            : 'grid md:grid-cols-3 gap-6 md:gap-8 items-start'
          }>
            {t.templates.map((tmpl: any, i: number) => {
              const Renderer = cardRenderers[i];
              const isQuoteReveal = i === 1;
              return (
                <motion.div
                  key={i}
                  className={`group cursor-pointer ${isMobile ? 'min-w-[85vw] snap-center flex-shrink-0' : ''}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15, duration: 0.6 }}
                  whileHover={{ y: -8, transition: { duration: 0.3 } }}
                  onClick={() => setActiveModal(i)}
                >
                  {/* Glass wrapper with breathing glow */}
                  <motion.div
                    className={`relative rounded-2xl overflow-hidden ${isQuoteReveal ? 'min-h-[340px] md:min-h-[380px]' : ''}`}
                    style={{
                      boxShadow: '0 8px 32px -8px hsl(var(--primary) / 0.1), 0 0 0 1px hsl(var(--primary) / 0.06)',
                    }}
                    animate={{
                      boxShadow: [
                        '0 8px 32px -8px hsl(var(--primary) / 0.08), 0 0 0 1px hsl(var(--primary) / 0.04)',
                        '0 12px 40px -8px hsl(var(--primary) / 0.18), 0 0 0 1px hsl(var(--primary) / 0.1)',
                        '0 8px 32px -8px hsl(var(--primary) / 0.08), 0 0 0 1px hsl(var(--primary) / 0.04)',
                      ],
                    }}
                    transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: i * 0.8 }}
                    whileHover={{
                      boxShadow: '0 20px 50px -12px hsl(var(--primary) / 0.25), 0 0 0 1px hsl(var(--primary) / 0.15)',
                    }}
                  >
                    {/* Glass overlay — border glow only, no blur to keep text crisp */}
                    <div
                      className="absolute inset-0 z-[1] pointer-events-none rounded-2xl"
                      style={{
                        border: '1px solid rgba(255,255,255,0.12)',
                      }}
                    />

                    <Renderer tmpl={tmpl} />

                    {/* Hover CTA overlay */}
                    <div className="absolute inset-x-0 bottom-0 z-[2] flex items-center justify-center pb-5 pt-10 bg-gradient-to-t from-black/50 via-black/15 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-b-2xl">
                      <span className="flex items-center gap-2 text-sm font-medium text-white/90 bg-white/15 backdrop-blur-md px-4 py-2 rounded-full border border-white/20 shadow-lg">
                        <Eye className="w-4 h-4" />
                        {t.preview}
                      </span>
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Full-screen modal */}
      {activeModal !== null && (
        <TemplatePreviewModal
          tmpl={t.templates[activeModal]}
          index={activeModal}
          onClose={() => setActiveModal(null)}
          closeLabel={t.close}
        />
      )}
    </>
  );
}
